/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useEffect, useState } from 'react';

/* Import configuration */
import {
    DEFAULT_TIME_SERIES_GROUP,
    EXAMPLE_TIME_SERIES_REQUEST,
} from '@/config/eeCheckConfig';

/* Import hooks */
import { useDemoSensorDeviceSelection } from '@/hooks/useDemoSensorDeviceSelection';

/* Import libraries */
import { toApiDateTimeValue, toDateTimeLocalValue } from '@/lib/formatting/dateTime';

/* Import types */
import type { TimeSeriesToMode } from '@/types/ui';

export function useTimeSeriesDemo(sensorCatalogVersion: number) {
    const selection = useDemoSensorDeviceSelection(DEFAULT_TIME_SERIES_GROUP, sensorCatalogVersion);
    const [timeSeriesTarget, setTimeSeriesTarget] = useState('');
    const [timeSeriesFrom, setTimeSeriesFrom] = useState(toDateTimeLocalValue(EXAMPLE_TIME_SERIES_REQUEST.from));
    const [timeSeriesToMode, setTimeSeriesToMode] = useState<TimeSeriesToMode>(EXAMPLE_TIME_SERIES_REQUEST.to === 'NOW' ? 'now' : 'custom');
    const [timeSeriesTo, setTimeSeriesTo] = useState(toDateTimeLocalValue(EXAMPLE_TIME_SERIES_REQUEST.to));
    const timeSeriesRequest = {
        ...EXAMPLE_TIME_SERIES_REQUEST,
        from: toApiDateTimeValue(timeSeriesFrom),
        to: timeSeriesToMode === 'now'
            ? 'NOW'
            : timeSeriesTo
                ? toApiDateTimeValue(timeSeriesTo)
                : 'NOW',
        urn: timeSeriesTarget,
    };

    useEffect(() => {
        setTimeSeriesTarget(selection.selectedSensorTarget);
    }, [selection.selectedSensorTarget]);

    return {
        deviceMetadata: selection.deviceMetadata,
        deviceSensor: selection.deviceSensor,
        deviceSensorCatalog: selection.deviceSensorCatalog,
        selectedTimeSeriesAvailability: selection.selectedAvailability,
        selectedTimeSeriesGroup: selection.selectedCustomer,
        selectedTimeSeriesGroupFilter: selection.selectedGroupFilter,
        selectedTimeSeriesMeter: selection.selectedDevice,
        selectedTimeSeriesSubsensor: selection.selectedSensorTarget,
        selectedTimeSeriesVirtualDeviceGroupBy: selection.selectedGroupBy,
        setSelectedTimeSeriesAvailability: selection.setSelectedAvailability,
        setSelectedTimeSeriesGroup: selection.setSelectedCustomer,
        setSelectedTimeSeriesGroupFilter: selection.setSelectedGroupFilter,
        setSelectedTimeSeriesMeter: selection.setSelectedDevice,
        setSelectedTimeSeriesSubsensor: selection.setSelectedSensorTarget,
        setSelectedTimeSeriesVirtualDeviceGroupBy: selection.setSelectedGroupBy,
        setTimeSeriesFrom,
        setTimeSeriesTarget,
        setTimeSeriesTo,
        setTimeSeriesToMode,
        timeSeriesFrom,
        timeSeriesRequest,
        timeSeriesSensorTargets: selection.sensorTargets,
        timeSeriesTarget,
        timeSeriesTo,
        timeSeriesToMode,
        timeSeriesVirtualDevices: selection.virtualDevices,
    };
}
